/**
 * Vue responsive du relevé pour l'affichage à l'écran (mobile/desktop).
 * En dark mode, optimisée pour la lisibilité sur petit écran.
 */
import React from 'react';
import { CheckCircle2, XCircle, Award, AlertTriangle, Trophy, FileSearch } from 'lucide-react';
import { ReleveData, ReleveUE, ReleveMatiere } from '../services/portailApi';

function getMention(moy: number | null | undefined): string {
  if (moy === null || moy === undefined) return '';
  if (moy >= 16) return 'Très Bien';
  if (moy >= 14) return 'Bien';
  if (moy >= 12) return 'Assez Bien';
  if (moy >= 10) return 'Passable';
  return '';
}

function fmt(v: number | null | undefined, dec = 2): string {
  if (v === null || v === undefined) return '—';
  return Number(v).toFixed(dec).replace('.', ',');
}

const NoteCell: React.FC<{ value: number | null | undefined; absent?: boolean; bold?: boolean }> = ({ value, absent, bold }) => {
  if (absent) return <span className="text-orange-400 font-semibold">ABS</span>;
  if (value === null || value === undefined) return <span className="text-gray-300">—</span>;
  const cls = value >= 10 ? 'text-green-400' : 'text-red-400';
  return <span className={`${cls} ${bold ? 'font-bold' : 'font-medium'}`}>{fmt(value)}</span>;
};

const MatiereLine: React.FC<{
  m: ReleveMatiere;
  onVoirScans?: (m: ReleveMatiere) => void;
}> = ({ m, onVoirScans }) => {
  const moyD = m.moy_devoirs;
  const moyDPond = moyD !== null && moyD !== undefined ? moyD * 0.4 : null;
  const noteEx = m.note_examen;
  const moyExPond = noteEx !== null && noteEx !== undefined ? noteEx * 0.6 : null;

  return (
    <tr className="border-b border-gray-800">
      <td className="px-3 py-2 text-[12px] text-gray-200">
        <div className="flex items-start gap-1.5">
          <div className="flex-1 min-w-0">
            <div className="font-medium">{m.nom_matiere}</div>
            <div className="text-[10px] text-gray-400 font-mono mt-0.5">{m.code_matiere}</div>
          </div>
          {onVoirScans && (
            <button
              onClick={() => onVoirScans(m)}
              title="Voir mes copies scannées (devoir + examen)"
              className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-900/40 hover:bg-blue-700 text-blue-200 hover:text-white transition-colors text-[10px] font-semibold"
              aria-label="Voir mes copies scannées"
            >
              <FileSearch size={12} />
              <span>Scan</span>
            </button>
          )}
        </div>
      </td>
      <td className="px-1 py-2 text-center text-[11px]"><NoteCell value={m.note_d1} absent={m.absent_d1} /></td>
      <td className="px-1 py-2 text-center text-[11px]"><NoteCell value={m.note_d2} absent={m.absent_d2} /></td>
      <td className="px-1 py-2 text-center text-[11px] text-gray-400">{fmt(moyDPond)}</td>
      <td className="px-1 py-2 text-center text-[11px]">
        <NoteCell value={noteEx} absent={m.absent_examen} />
        {/* Détail S1 / Rattrapage si les deux existent */}
        {m.note_examen_rattrapage !== null && m.note_examen_rattrapage !== undefined && (
          <div className="text-[9px] text-gray-500 mt-0.5 leading-tight">
            <span title="Session ordinaire">S1: {fmt(m.note_examen_s1)}</span>
            {' / '}
            <span className={`${m.est_rattrapage ? 'text-blue-400 font-semibold' : ''}`} title="Session rattrapage">
              R: {fmt(m.note_examen_rattrapage)}
            </span>
          </div>
        )}
        {m.est_rattrapage && (m.note_examen_rattrapage === null || m.note_examen_rattrapage === undefined) === false && (
          <div className="text-[9px] text-blue-400 mt-0.5 italic">↻ rattrapée</div>
        )}
      </td>
      <td className="px-1 py-2 text-center text-[11px] text-gray-400">{fmt(moyExPond)}</td>
      <td className="px-1 py-2 text-center text-[11px] text-gray-400">{fmt(m.credits, 1)}</td>
      <td className="px-1 py-2 text-center text-[11px]"><NoteCell value={m.moyenne_ecue} bold /></td>
    </tr>
  );
};

const UEBlock: React.FC<{ ue: ReleveUE; onVoirScans?: (m: ReleveMatiere) => void; masque?: boolean }> = ({ ue, onVoirScans, masque }) => {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
      <div className={`px-3 py-2 flex items-center justify-between ${
        masque
          ? 'bg-gray-800/60 border-b border-gray-700'
          : ue.validee
            ? 'bg-green-900/30 border-b border-green-800/50'
            : 'bg-orange-900/30 border-b border-orange-800/50'
      }`}>
        <div className="flex-1 min-w-0">
          {/* Fix 2026-09-10 : afficher le nom_ue en priorité — le nom
              contient déjà « UE1, UE2, … » ; le code technique n'apporte
              rien à l'étudiant. */}
          <div className="font-bold text-sm text-gray-100 truncate">
            {(() => {
              const nom = (ue.nom_ue || '').trim();
              if (/^UE\s?\d/i.test(nom)) return nom;
              return nom || ue.code_ue;
            })()}
          </div>
          <div className="text-[10px] font-mono text-gray-500 truncate">{ue.code_ue}</div>
          {ue.validee && !masque && (
            <div className="text-[10px] mt-0.5 inline-flex items-center gap-1">
              {ue.session_label === 'compensation' || ue.validee_par_compensation ? (
                <span className="px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-300 border border-amber-800/50">
                  Validée par compensation
                </span>
              ) : ue.session_label === 'rattrapage' || ue.validee_en_rattrapage ? (
                <span className="px-1.5 py-0.5 rounded bg-blue-900/40 text-blue-300 border border-blue-800/50">
                  Validée en rattrapage
                </span>
              ) : (
                <span className="px-1.5 py-0.5 rounded bg-green-900/40 text-green-300 border border-green-800/50">
                  Validée en session ordinaire
                </span>
              )}
            </div>
          )}
        </div>
        {masque ? (
          <div className="text-right ml-2 flex-shrink-0 text-[10px] text-gray-400">
            {fmt(ue.credits_total, 1)} cr.
          </div>
        ) : (
          <>
            <div className="text-right ml-2 flex-shrink-0">
              <div className="font-bold text-base">
                <NoteCell value={ue.moyenne_ue} bold />
              </div>
              <div className="text-[10px] text-gray-400">
                {fmt(ue.credits_obtenus, 1)}/{fmt(ue.credits_total, 1)} cr.
              </div>
            </div>
            <div className="ml-3 flex-shrink-0">
              {ue.validee ? (
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              ) : (
                <XCircle className="w-5 h-5 text-red-400" />
              )}
            </div>
          </>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-800/50 text-[9px] uppercase tracking-wider text-gray-400">
              <th className="px-3 py-1.5 text-left">Matière</th>
              <th className="px-1 py-1.5 w-[40px]">D1</th>
              <th className="px-1 py-1.5 w-[40px]">D2</th>
              <th className="px-1 py-1.5 w-[42px]">Moy.D</th>
              <th className="px-1 py-1.5 w-[42px]">Examen</th>
              <th className="px-1 py-1.5 w-[42px]">Moy.E</th>
              <th className="px-1 py-1.5 w-[36px]">Cr.</th>
              <th className="px-1 py-1.5 w-[42px]">ECUE</th>
            </tr>
          </thead>
          <tbody>
            {ue.matieres.map((m) => <MatiereLine key={m.matiere_id} m={m} onVoirScans={onVoirScans} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface Props {
  data: ReleveData;
  onVoirScans?: (m: ReleveMatiere) => void;
}

export const ReleveResponsive: React.FC<Props> = ({ data, onVoirScans }) => {
  const etu = data.etudiant;
  const res = data.resultat_semestre;
  const ues = data.ues || [];
  const moyenne = res?.moyenne_semestre;
  const mention = getMention(moyenne);
  const valide = res?.valide;
  // Fix 2026-09-12 : notes d'examens masquées → résultat neutre (ni décision, ni moyenne, ni rang)
  const masque = !!data.examens_masques;

  return (
    <div className="space-y-3">
      {/* Header relevé compacté */}
      <div className="bg-gradient-to-br from-blue-900 to-blue-950 border border-blue-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-blue-700/40 border-b border-blue-800 flex items-center justify-between">
          <div className="font-bold text-white tracking-wide text-sm">RELEVÉ DE NOTES</div>
          <div className="text-xs px-2 py-0.5 bg-blue-600 text-white rounded font-semibold">Semestre {data.semestre}</div>
        </div>
        <div className="p-4 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <div className="text-gray-400">Année académique</div>
          <div className="text-gray-100 font-medium text-right">{data.annee_academique}</div>
          <div className="text-gray-400">Cycle</div>
          <div className="text-gray-100 font-medium text-right truncate">{etu.cycle || 'LICENCE PRO'}</div>
          <div className="text-gray-400">Niveau / Option</div>
          <div className="text-gray-100 font-medium text-right truncate">{etu.classe || etu.niveau}</div>
        </div>
      </div>

      {/* Résultat global */}
      {res && masque && (
        <div className="rounded-xl border border-gray-700 bg-gray-900 p-4">
          <div className="text-xs text-gray-300 uppercase tracking-wide">Résultat du semestre</div>
          <div className="text-sm text-gray-400 mt-1">
            En attente de la publication des notes d'examens.
          </div>
        </div>
      )}
      {res && !masque && (
        <div className={`rounded-xl border overflow-hidden ${
          valide
            ? 'bg-gradient-to-br from-green-900/40 to-green-950/40 border-green-800'
            : 'bg-gradient-to-br from-orange-900/40 to-red-950/40 border-orange-800'
        }`}>
          <div className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  {valide ? (
                    <Trophy className="w-5 h-5 text-yellow-400" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-orange-400" />
                  )}
                  <div className="text-xs text-gray-300 uppercase tracking-wide">
                    {valide ? 'Semestre validé' : 'Semestre non validé'}
                  </div>
                </div>
                <div className="text-4xl font-black mt-1">
                  <NoteCell value={moyenne} bold />
                  <span className="text-base font-normal text-gray-400">/20</span>
                </div>
                {mention && (
                  <div className={`mt-1.5 inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold border ${
                    mention === 'Très Bien'
                      ? 'bg-amber-500/15 border-amber-500/40 text-amber-300'
                      : mention === 'Bien'
                        ? 'bg-blue-500/15 border-blue-500/40 text-blue-300'
                        : 'bg-gray-700/50 border-gray-600/40 text-gray-300'
                  }`}>
                    <Award size={13} />
                    Mention {mention}
                  </div>
                )}
              </div>
              <div className="text-right text-sm">
                <div className="text-gray-400 text-xs">Crédits</div>
                <div className="text-white font-bold text-2xl">
                  {res.credits_obtenus}<span className="text-gray-400 text-sm">/{res.credits_requis}</span>
                </div>
                {res.rang && (
                  <div className="text-xs text-gray-400 mt-1">
                    Rang {res.rang}{res.effectif_classe ? `/${res.effectif_classe}` : ''}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Note d'aide pour le bouton Scan */}
      {onVoirScans && (
        <div className="bg-blue-900/30 border border-blue-800 rounded-lg p-3 text-xs text-blue-200 flex items-start gap-2">
          <FileSearch size={14} className="text-blue-400 flex-shrink-0 mt-0.5" />
          <span>
            Cliquez sur le bouton <strong className="text-blue-100 bg-blue-700 px-1.5 py-0.5 rounded">Scan</strong> à côté
            du nom d'une matière pour voir la copie scannée correspondante (devoir / examen / rattrapage).
          </span>
        </div>
      )}

      {/* UEs */}
      <div className="space-y-2">
        {ues.map((ue) => <UEBlock key={ue.ue_id} ue={ue} onVoirScans={onVoirScans} masque={masque} />)}
      </div>

      {/* Provisoire */}
      <div className="text-center text-xs italic text-orange-400/80 border-t border-dashed border-orange-800/40 pt-3 mt-3">
        Ce relevé est PROVISOIRE et susceptible d'être modifié après délibération définitive.
      </div>
    </div>
  );
};
